import React from 'react';
import L from 'leaflet';
import 'leaflet-draw';
import Config from '../../constants/appConfig';
import { Typography, IconButton, Tooltip } from '@material-ui/core';
import { FirstPage, LastPage, ChevronLeft, ChevronRight, Info } from '@material-ui/icons';
import geo from '../../components/util/geo';
import Units from '../../components/util/units';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import Moment from 'moment';

class DiaryLocation extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      airplanes: props.airplanes || [],
      currentAirplaneIndex: 0,
      popupsOpen: false,
    };
    this.map = null;
    this.mapMarkers = [];
    this.mapContainerRef = React.createRef();
    this.resizeObserver = null;
    this.featureGroupPoly = L.featureGroup();
  }

  componentDidMount() {
    this.initializeMap();
    this.getPlanePositions(this.state.airplanes);

    const savedPopupsOpen = JSON.parse(localStorage.getItem('popupsOpen'));
    if (savedPopupsOpen !== null) {
      this.setState({ popupsOpen: savedPopupsOpen }, () => {
        if (this.state.popupsOpen) {
          setTimeout(() => {
            this.mapMarkers.forEach(({ marker }) => {
              marker.openPopup();
            });
          }, 1500);
        }
      });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.airplanes !== this.props.airplanes) {
      this.setState({ airplanes: this.props.airplanes, currentAirplaneIndex: 0 });
      this.getPlanePositions(this.props.airplanes);
    }

    if (prevState.currentAirplaneIndex !== this.state.currentAirplaneIndex) {
      this.focusOnCurrentAirplane();
    }
  }

  componentWillUnmount() {
    this.savePopupsState();
    this.cleanupMap();
  }

  savePopupsState = () => {
    const popupsState = this.mapMarkers.map(({ marker }) => marker.isPopupOpen());
    localStorage.setItem('popupsState', JSON.stringify(popupsState));
  };

  initializeMap = () => {
    if (this.map) return;

    this.map = L.map(this.mapContainerRef.current, {
      zoomControl: false,
      attributionControl: false,
    }).setView([-30.033056, -51.230000], 12);

    L.tileLayer(Config.tile_google, Config.tile_google_config).addTo(this.map);

    this.resizeObserver = new ResizeObserver(() => {
      this.map.invalidateSize();
    });

    if (this.mapContainerRef.current) {
      this.resizeObserver.observe(this.mapContainerRef.current);
    }

    this.featureGroupPoly.addTo(this.map);
    this.map.on('popupclose', this.closeAllPopups);
  };

  cleanupMap = () => {
    if (this.map) {
      this.clearMapMarkers();
      this.map.remove();
      this.map = null;
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  };

  getPlanePositions = (airplanes) => {
    this.savePopupsState();
    if (!Array.isArray(airplanes) || airplanes.length === 0) {
      return;
    }
    this.clearMapMarkers();

    const savedPopupsState = JSON.parse(localStorage.getItem('popupsState')) || [];

    airplanes.forEach((airplane, index) => {
      if (airplane.id_dgps) {
        const apiUrl = `${Config.server}/plane_position/${airplane.id_dgps}`;
        fetch(apiUrl, {
          method: 'GET',
          headers: {
            Authorization: localStorage.getItem('access-token-jwt'),
          },
        })
          .then((res) => res.json())
          .then((positionData) => {
            if (positionData.length > 0) {
              const planePosition = positionData[0];
              const svgConfig = airplane.svg ? JSON.parse(airplane.svg) : {};
              this.addMarkerToMap(airplane, planePosition, svgConfig, index);

              const marker = this.mapMarkers[index].marker;
              if (savedPopupsState[index]) {
                marker.openPopup();
              }
            }
          })
          .catch((error) => {
            console.error(error);
          });
      }
    });
  };

  addMarkerToMap = (airplane, position, svgConfig, index) => {
    const { lat, lng, angle } = position;

    if (lat && lng) {
      const svgIcon = L.divIcon({
        popupAnchor: [0, -20],
        className: 'leaflet-hide-icon2',
        html: `<svg viewBox="-200 -200 2560 1820" width="128" height="61" style="transform: translate(-58px, -27px) rotate(${angle || 0}deg);">
        <path display="${svgConfig.showBackground == '2' && 'none'}" fill="${svgConfig.background}" d="M921.79 423.76l-769.47 0c-89.99,-1.59 -147.49,58.49 -142.52,151.01l16.37 217.02 903.71 0 52.81 366.09 -244.02 0c-50.85,0.91 -97.98,36.57 -87.52,97.53l29.27 140.68 203.46 -0.16 0 28.11 145.18 0 54.71 82.61 55.16 -82.61 144.73 0 -0.01 -28.11 203.47 0.16 29.26 -140.68c10.47,-60.96 -36.66,-96.62 -87.52,-97.53l-244.01 0 52.8 -366.09 903.72 0 16.37 -217.02c4.96,-92.52 -52.53,-152.6 -142.52,-151.01l-769.47 0c-20.22,-203.91 -74.48,-326.49 -161.98,-412.03 -87.51,85.54 -141.77,208.12 -161.98,412.03z"/>
        <path display="${svgConfig.showBackground == '2' && 'none'}" fill="${svgConfig.border}" d="M921.79 434.85l-769.58 -0.03c-4.38,-0.07 -8.76,0.01 -13.11,0.24 -36.97,1.98 -67.35,15.39 -88.59,37.73 -21.27,22.36 -33.52,53.84 -34.19,91.92 -0.08,4.45 0.01,9.09 0.26,13.87l17.6 206.39 903.24 0c18.33,121.49 34.93,262.49 53.18,384l-251.93 0c-5.15,0.06 -10.35,0.57 -15.5,1.45 -18.47,3.16 -35.43,11.34 -47.6,23.95 -12.04,12.46 -19.46,29.35 -19.09,50.13 0.08,4.42 0.53,9.1 1.38,14.03l28.18 130.4 204.63 -0.16 0 28.08 142.03 0 51.1 77.16 51.51 -77.16 141.56 0 -0.01 -28.08 204.64 0.16 28.18 -130.4c0.85,-4.93 1.3,-9.61 1.37,-14.03 0.37,-20.78 -7.05,-37.67 -19.08,-50.13 -12.17,-12.6 -29.14,-20.78 -47.6,-23.95 -5.15,-0.88 -10.35,-1.39 -15.51,-1.48l-251.92 0.03 56.53 -384 903.24 0 14.24 -206.39c0.26,-4.78 0.35,-9.42 0.27,-13.88 -0.67,-38.07 -12.93,-69.55 -34.19,-91.92 -21.24,-22.33 -51.62,-35.74 -88.59,-37.73 -4.36,-0.22 -8.74,-0.3 -13.11,-0.23l-775.73 0.03 -0.61 -6.15c-10.04,-101.29 -28.51,-182.31 -55.31,-248.55 -25.66,-63.45 -59,-113.36 -99.91,-154.58 -40.91,41.23 -74.26,91.13 -99.92,154.58 -26.79,66.24 -45.26,147.26 -55.3,248.55l-0.61 6.15 -6.15 0zm-769.47 -13.6l763.33 -0.03c10.31,-99.77 28.87,-180.07 55.59,-246.12 27.41,-67.77 63.38,-120.56 107.79,-163.97l4.73 -4.63 4.75 4.63c44.41,43.41 80.38,96.2 107.79,163.97 26.72,66.05 45.28,146.35 55.58,246.12l763.34 0c4.79,-0.05 9.43,0.03 13.91,0.26 40.59,2.18 74.12,17.09 97.75,41.94 23.59,24.81 37.18,59.43 37.91,101.07 0.09,4.94 0,9.92 -0.27,14.89 0,72.08 -9.89,145.09 -15.14,219.21l-904.19 0 -52.43 356.75 236.21 0.03c6.04,0.1 11.97,0.66 17.69,1.64 21.22,3.64 40.84,13.18 55.1,27.94 14.41,14.92 23.3,35.08 22.87,59.77 -0.1,5.24 -0.6,10.62 -1.54,16.11 -10.3,52.77 -20.23,88.86 -30.35,141.68l-202.3 -0.16 0 28.13 -147.9 0 -58.8 88.07 -58.32 -88.07 -148.33 0 0.01 -28.13 -202.31 0.16c-10.08,-52.82 -20.05,-88.91 -30.34,-141.68 -0.94,-5.49 -1.45,-10.87 -1.54,-16.11 -0.44,-24.69 8.45,-44.85 22.86,-59.77 14.27,-14.76 33.89,-24.3 55.11,-27.94 5.72,-0.98 11.65,-1.54 17.69,-1.64l236.2 -0.03 -49.07 -356.75 -904.19 0c-5.22,-74.18 -18.5,-147.06 -18.5,-219.21 -0.26,-4.97 -0.35,-9.94 -0.26,-14.89 0.73,-41.63 14.32,-76.26 37.91,-101.07 23.63,-24.85 57.16,-39.75 97.75,-41.93 4.46,-0.24 9.11,-0.32 13.91,-0.24z"/>
  
        <path fill="${svgConfig.wings}" d="M79.81 737.13l1003.97 0 1003.96 0c2.26,-65.52 7.02,-131.94 6.79,-196.55 -0.12,-35.36 -28.93,-64.29 -64.28,-64.29l-946.47 0 -946.47 0c-35.36,0 -64.16,28.93 -64.29,64.29 -0.22,64.61 4.53,131.03 6.79,196.55z"/>
  
        <path fill="${svgConfig.front}" d="M1083.78 85.52l35.03 74.63c25.94,65.15 63.11,243.78 62.78,289.46l0 210.12c0.16,17.48 -1.86,45.3 -5.47,80.6 -0.27,2.71 -0.56,5.46 -0.85,8.26l-91.49 134.3 -91.43 -133.72c-0.19,-1.79 -0.39,-3.59 -0.57,-5.35 -3.83,-36.98 -5.99,-66.03 -5.82,-84.09l0 -210.12c-0.33,-45.68 36.84,-224.31 62.77,-289.46l35.05 -74.63zm0 797.37l0 -140.81 0 -656.56z"/>
        <path fill="${svgConfig.back}" d="M1083.78 1425.95c-4.34,-33.99 -9.28,-70.8 -14.57,-109.29l-16.26 0 0 20.67 -3.34 0 0 8.65c0.07,0.2 0.1,0.41 0.1,0.64l0 10.41c0,1.13 -0.91,2.04 -2.03,2.04l-118.2 0c-1.12,0 -2.03,-0.91 -2.03,-2.04l0 -9.96 -207.76 0c-6.49,-27.03 -12.53,-94.83 -12.68,-128.66 -0.04,-7.19 5.88,-13.08 13.07,-13.08l333.57 0c-23.65,-167.24 -49.58,-345.3 -61.3,-456.16l91.43 133.72 91.49 -134.3c-11.7,110.83 -37.68,289.21 -61.37,456.74l333.57 0c7.19,0 13.1,5.89 13.08,13.08 -0.16,33.83 -6.19,101.63 -12.68,128.66l-207.76 0 0 9.96c0,1.13 -0.92,2.04 -2.04,2.04l-118.2 0c-1.12,0 -2.03,-0.91 -2.03,-2.04l0 -10.41c0,-0.23 0.03,-0.44 0.1,-0.64l0 -8.65 -3.34 0 0 -20.67 -16.26 0c-5.29,38.49 -10.22,75.3 -14.56,109.29z"/>
  
        <path fill="${svgConfig.frontDetail}" d="M1011.56 409.09c20.76,-140.05 45.74,-210.78 72.22,-256.44 26.47,45.66 51.45,116.39 72.2,256.44 -14.21,-2.68 -26.92,-4.38 -43.33,-5.2 -18.41,0 -36.82,0 -55.23,0 -15.73,0.49 -31.03,2.15 -45.86,5.2z"/>
        <polygon fill="${svgConfig.propeller}" points="1083.78,85.53 1108.69,138.38 1058.86,138.38 "/>
  
        <polygon fill="${svgConfig.middle}" points="1083.78,914.26 994.37,767.77 991.68,742.84 988.48,709.01 1083.78,859.28 1179.09,708.74 1176.07,740.86 1173.23,767.35 "/>
  
        <path fill="${svgConfig.hoper}" d="M1011.56 409.09c14.82,-3.05 30.13,-4.72 45.85,-5.2 18.42,0 36.83,0 55.25,0 16.41,0.81 29.12,2.52 43.32,5.2l6.12 44.24 0 20.52 0 171.73 -5.57 0c-4.08,-15.55 -16.12,-25.51 -33.99,-29.18 -12.84,-1.47 -25.77,-2.16 -38.76,-2.27l0 0c-12.74,-0.13 -25.63,0.43 -38.82,2.27 -18.9,3.82 -30.38,13.63 -33.95,29.18l-5.56 0 0 -171.16 0 -21.09 6.11 -44.24z"/>
  
        <path stroke="${svgConfig.hoperDetail}" d="M1050.59 457.08l66.83 0c1.21,0 2.31,0.5 3.11,1.29 0.79,0.8 1.29,1.9 1.29,3.11l0 93.55c0,1.2 -0.5,2.3 -1.29,3.1 -0.8,0.8 -1.9,1.3 -3.11,1.3l-66.83 0c-1.21,0 -2.31,-0.5 -3.1,-1.3l-0.01 0c-0.79,-0.8 -1.29,-1.9 -1.29,-3.1l0 -93.55c0,-1.21 0.5,-2.31 1.29,-3.11 0.8,-0.79 1.9,-1.29 3.11,-1.29zm66.83 3.62l-66.83 0c-0.22,0 -0.41,0.09 -0.55,0.23 -0.14,0.14 -0.23,0.34 -0.23,0.55l0 93.55c0,0.21 0.09,0.41 0.23,0.55 0.14,0.14 0.33,0.22 0.55,0.22l66.83 0c0.21,0 0.41,-0.09 0.55,-0.23 0.14,-0.13 0.22,-0.33 0.22,-0.54l0 -93.55c0,-0.21 -0.08,-0.41 -0.22,-0.55 -0.14,-0.14 -0.34,-0.23 -0.55,-0.23z"/>
        <rect stroke="${svgConfig.hoperDetail}" x="1052.3" y="467.95" width="8.38" height="79.93"/>
        <rect stroke="${svgConfig.hoperDetail}" x="1036.91" y="467.95" width="6.34" height="79.93"/>
  
        <circle fill="${svgConfig.hoperDetail}" cx="1053.58" cy="463.93" r="1.19"/>
        <circle fill="${svgConfig.hoperDetail}" cx="1059.07" cy="463.93" r="1.19"/>
        <circle fill="${svgConfig.hoperDetail}" cx="1064.56" cy="463.93" r="1.19"/>
        <circle fill="${svgConfig.hoperDetail}" cx="1070.05" cy="463.93" r="1.19"/>
        <circle fill="${svgConfig.hoperDetail}" cx="1075.54" cy="463.93" r="1.19"/>
        <circle fill="${svgConfig.hoperDetail}" cx="1081.03" cy="463.93" r="1.19"/>
        <circle fill="${svgConfig.hoperDetail}" cx="1086.52" cy="463.93" r="1.19"/>
        <circle fill="${svgConfig.hoperDetail}" cx="1092.01" cy="463.93" r="1.19"/>
        <circle fill="${svgConfig.hoperDetail}" cx="1097.5" cy="463.93" r="1.19"/>
        <circle fill="${svgConfig.hoperDetail}" cx="1102.99" cy="463.93" r="1.19"/>
        <circle fill="${svgConfig.hoperDetail}" cx="1108.48" cy="463.93" r="1.19"/>
        <circle fill="${svgConfig.hoperDetail}" cx="1113.97" cy="463.93" r="1.19"/>
  
        <circle fill="${svgConfig.hoperDetail}" cx="1053.58" cy="552.46" r="1.19"/>
        <circle fill="${svgConfig.hoperDetail}" cx="1059.07" cy="552.46" r="1.19"/>
        <circle fill="${svgConfig.hoperDetail}" cx="1064.56" cy="552.46" r="1.19"/>
        <circle fill="${svgConfig.hoperDetail}" cx="1070.05" cy="552.46" r="1.19"/>
        <circle fill="${svgConfig.hoperDetail}" cx="1075.54" cy="552.46" r="1.19"/>
        <circle fill="${svgConfig.hoperDetail}" cx="1081.03" cy="552.46" r="1.19"/>
        <circle fill="${svgConfig.hoperDetail}" cx="1086.52" cy="552.46" r="1.19"/>
        <circle fill="${svgConfig.hoperDetail}" cx="1092.01" cy="552.46" r="1.19"/>
        <circle fill="${svgConfig.hoperDetail}" cx="1097.5" cy="552.46" r="1.19"/>
        <circle fill="${svgConfig.hoperDetail}" cx="1102.99" cy="552.46" r="1.19"/>
        <circle fill="${svgConfig.hoperDetail}" cx="1108.48" cy="552.46" r="1.19"/>
        <circle fill="${svgConfig.hoperDetail}" cx="1113.97" cy="552.46" r="1.19"/>
  
        <path display="${(svgConfig.windowsType == '1' || svgConfig.windowsType == 3) && 'none'}" fill="${svgConfig.windowsColor}" d="M1055.24 679.17l-13.61 -53.16c-6.5,1.58 -12.08,4.8 -16.24,10.48 -4.53,6.18 -7.46,15.32 -8.22,28.44l26.21 31.9c0.41,-4 1.54,-7.45 3.16,-10.24 2.18,-3.73 5.26,-6.29 8.7,-7.42zm-10.53 -55.74l14.98 58.5 -1.94 0.33c-3.15,0.54 -6.06,2.7 -8.08,6.16 -1.94,3.32 -3.05,7.8 -2.82,13.21l0.24 5.4c-11.2,-13.63 -22.39,-27.27 -33.6,-40.89l0.04 -0.7c0.73,-14.26 3.93,-24.24 8.95,-31.09 5.11,-6.98 12.04,-10.67 20.13,-12.25l1.67 -0.33 0.43 1.66z"/>
        <path display="${(svgConfig.windowsType == '1' || svgConfig.windowsType == 3) && 'none'}" fill="${svgConfig.windowsColor}" d="M1063.79 680.41l-15.06 -60.21 70.75 0c-5.12,20.53 -10.25,41.06 -15.39,61.58l-39.96 0 -0.34 -1.37zm-10.42 -56.59l13.58 54.34 15.35 0 18.96 0 13.59 -54.34 -32.55 0 -28.93 0z"/>
        <path display="${(svgConfig.windowsType == '1' || svgConfig.windowsType == 3) && 'none'}" fill="${svgConfig.windowsColor}" d="M1109.02 680.03l14.91 -58.26 1.68 0.33c8.09,1.58 15.02,5.27 20.13,12.25 5.02,6.85 8.22,16.83 8.95,31.09l0.04 0.7 -33.6 40.89 0.23 -5.4c0.24,-5.41 -0.87,-9.89 -2.81,-13.21 -2.02,-3.46 -4.94,-5.62 -8.08,-6.16l-1.94 -0.33 0.49 -1.9zm17.56 -54.02l-13.6 53.16c3.44,1.13 6.51,3.69 8.7,7.42 1.62,2.79 2.75,6.24 3.16,10.24l26.21 -31.9c-0.76,-13.12 -3.69,-22.26 -8.22,-28.44 -4.16,-5.68 -9.74,-8.9 -16.25,-10.48z"/>
        <path display="${(svgConfig.windowsType == '1' || svgConfig.windowsType == 3) && 'none'}" fill="${svgConfig.windowsColor}" d="M1142.15 771.73l8.37 -88.79 -24.94 32.13 -2.27 65.54 18.84 -8.88zm12.55 -94.63l-9.15 97.03 -26.28 12.38c1.6,-22.64 1.21,-50.15 2.77,-72.79 13.48,-17.32 19.79,-25.48 33.25,-42.81l-0.59 6.19z"/>
        <path display="${(svgConfig.windowsType == '1' || svgConfig.windowsType == 3) && 'none'}" fill="${svgConfig.windowsColor}" d="M1026.63 771.73l-8.38 -88.79 24.95 32.13 2.26 65.54 -18.83 -8.88zm-12.56 -94.63l9.16 97.03 26.28 12.38c-1.6,-22.64 -1.22,-50.15 -2.78,-72.79 -13.47,-17.32 -19.78,-25.48 -33.24,-42.81l0.58 6.19z"/>
        <path display="${(svgConfig.windowsType == '1' || svgConfig.windowsType == 3) && 'none'}" fill="${svgConfig.windowsColor2}" d="M1057.45 680.48l-14.49 -56.61c-15.32,3 -26.2,13.9 -27.63,41.66l29.72 36.18c-0.53,-12.11 5.36,-20.03 12.4,-21.23z"/>
        <polygon display="${(svgConfig.windowsType == '1' || svgConfig.windowsType == 3) && 'none'}" fill="${svgConfig.windowsColor2}" points="1065.54,679.97 1051.05,622.01 1084.11,622.01 1117.17,622.01 1102.68,679.97 1084.11,679.97 "/>
        <path display="${(svgConfig.windowsType == '1' || svgConfig.windowsType == 3) && 'none'}" fill="${svgConfig.windowsColor2}" d="M1110.77 680.48l14.49 -56.61c15.32,3 26.2,13.9 27.63,41.66l-29.72 36.18c0.53,-12.11 -5.37,-20.03 -12.4,-21.23z"/>
        <polygon display="${(svgConfig.windowsType == '1' || svgConfig.windowsType == 3) && 'none'}" fill="${svgConfig.windowsColor2}" points="1143.85,772.93 1152.9,676.92 1123.81,714.39 1121.29,783.56 "/>
        <polygon display="${(svgConfig.windowsType == '1' || svgConfig.windowsType == 3) && 'none'}" fill="${svgConfig.windowsColor2}" points="1024.93,772.93 1015.87,676.92 1044.96,714.39 1047.48,783.56 "/>
  
        <path display="${(svgConfig.windowsType == '2' || svgConfig.windowsType == 3) && 'none'}" fill="${svgConfig.windowsColor}" d="M1044.24 706.82l-32.71 -39.55 0.02 -0.69c0.48,-12.89 4.21,-23.72 9.78,-31.53 6.07,-8.5 14.33,-13.47 23,-13.71 22.73,0 60.01,-2.65 80.87,-0.4 8.07,0.98 16.46,6.63 22.47,15.49 5.26,7.76 8.71,18.01 8.53,29.74l-0.01 0.63c-12.13,14.72 -24.22,29.48 -36.33,44.22l0.16 -5.29c0.18,-5.99 -1.25,-11.67 -3.86,-15.84 -2.37,-3.77 -5.69,-6.27 -9.63,-6.52l-47.75 -0.39c-4.39,0.03 -7.17,2.99 -8.86,7.06 -1.86,4.46 -2.47,10.2 -2.47,15.18l0.01 5.49 -3.22 -3.89zm-29.05 -40.8l28.79 34.81c0.3,-4.18 1.07,-8.52 2.6,-12.17 2.23,-5.35 6.02,-9.25 12.19,-9.29 0.03,-0.03 43.86,0.39 47.99,0.39 5.19,0.33 9.48,3.48 12.45,8.22 2.25,3.58 3.73,8.11 4.24,12.98l29.14 -35.47c0.05,-10.68 -3.13,-19.99 -7.91,-27.04 -5.39,-7.96 -12.8,-13.03 -19.85,-13.9l-80.4 0.4c-7.51,0.21 -14.75,4.62 -20.15,12.2 -5.09,7.13 -8.54,17.04 -9.09,28.87z"/>
        <path display="${(svgConfig.windowsType == '2' || svgConfig.windowsType == 3) && 'none'}" fill="${svgConfig.windowsColor}" d="M1148.13 746.94l5.19 -65.02 -30.19 35.86 -2.98 42.35 27.98 -13.19zm3.4 2.4l-35.42 16.69c1.6,-22.64 1.92,-26.96 3.49,-49.6 13.47,-17.31 23.24,-27.88 36.69,-45.21l-4.76 78.12z"/>
        <path display="${(svgConfig.windowsType == '2' || svgConfig.windowsType == 3) && 'none'}" fill="${svgConfig.windowsColor}" d="M1019.72 746.94l-5.19 -65.02 30.19 35.86 2.98 42.35 -27.98 -13.19zm-3.4 2.4l35.42 16.69c-1.6,-22.64 -1.93,-26.96 -3.49,-49.6 -13.47,-17.31 -23.24,-27.88 -36.7,-45.21l4.77 78.12z"/>
        <path display="${(svgConfig.windowsType == '2' || svgConfig.windowsType == 3) && 'none'}" fill="${svgConfig.windowsColor2}" d="M1045.64 705.67l-32.28 -39.03c0.95,-25.86 15.34,-43.06 31.02,-43.49l80.61 -0.41c14.29,1.72 29.79,19.3 29.4,43.4l-32.57 39.64c0.38,-12.38 -5.82,-23.61 -15.18,-24.22l-47.77 -0.39c-10.8,0 -13.31,14.09 -13.23,24.5z"/>
        <polygon display="${(svgConfig.windowsType == '2' || svgConfig.windowsType == 3) && 'none'}" fill="${svgConfig.windowsColor2}" points="1149.83,748.14 1154.47,677.26 1121.36,717.11 1118.13,763.08 "/>
        <polygon display="${(svgConfig.windowsType == '2' || svgConfig.windowsType == 3) && 'none'}" fill="${svgConfig.windowsColor2}" points="1018.02,748.14 1013.38,677.26 1046.49,717.11 1049.72,763.08 "/>
  
        <path fill="${svgConfig.wingsDetail}" d="M2042.28 564.14c-0.91,-40.69 -3.41,-76.21 -13.01,-87.85l-52.01 0c6.43,3.66 10.08,38.6 12.38,88.76l1.24 80.15c0.17,33.64 -0.74,63.72 -4.7,91.93l51.39 0c5.74,-52.56 4.55,-79.94 4.71,-92.84l0 -80.15z"/>
        <path fill="${svgConfig.wingsDetail}" d="M125.37 564.14c0.91,-40.69 3.41,-76.21 13,-87.85l52.02 0c-6.43,3.66 -10.09,38.6 -12.39,88.76l-1.24 80.15c-0.16,33.64 0.75,63.72 4.71,91.93l-51.4 0c-5.74,-52.56 -4.54,-79.94 -4.7,-92.84l0 -80.15z"/>
  
        <rect fill="${svgConfig.wingsDetail2}" x="940.22" y="476.29" width="45.27" height="211.93"/>
        <rect fill="${svgConfig.wingsDetail2}" x="1182.06" y="476.29" width="45.27" height="211.93"/>
  
        <rect fill="${svgConfig.wingsDetail3}" x="1230.52" y="558.44" width="45.27" height="79.59"/>
        <rect fill="${svgConfig.wingsDetail3}" x="891.77" y="558.44" width="45.27" height="79.59"/>
  
        <path display="${svgConfig.tailType == '2' && 'none'}" fill="${svgConfig.tailColor}" d="M1078.05 1350.01l5.73 75.94 5.72 -75.94 0 -163.93c-2.44,-2.73 -4.68,-4.2 -5.72,-4.08 -1.04,-0.12 -3.29,1.35 -5.73,4.08l0 163.93z"/>
        <path display="${svgConfig.tailType == '2' && 'none'}" fill="${svgConfig.tailColor}" d="M1073.32 1191.54l4.73 -5.46 0 163.93 3.53 46.88 -6.42 -47.16 -1.84 -158.19zm20.91 0l-4.73 -5.46 0 163.93 -3.53 46.88 6.42 -47.16 1.84 -158.19z"/>
  
        <path display="${svgConfig.tailType == '1' && 'none'}" fill="${svgConfig.tailColor}" d="M1076.96 1293.27l2.33 -108.35c-4.63,0.04 -16.67,-7.43 -22.33,-15.64l20 123.99z"/>
        <path display="${svgConfig.tailType == '1' && 'none'}" fill="${svgConfig.tailColor}" d="M1090.63 1293.27l-2.53 -108.35c5.05,0.04 18.19,-7.43 24.37,-15.64l-21.84 123.99z"/>
  
        <path display="${svgConfig.tailType == '1' && 'none'}" fill="${svgConfig.tailColor2}" d="M1056.45 1162.47l-1.81 -24.64c5.71,9.51 14.16,16.97 25.03,22.61l0.11 19.64c-8.86,-2.31 -18.22,-9.7 -23.33,-17.61z"/>
        <path display="${svgConfig.tailType == '1' && 'none'}" fill="${svgConfig.tailColor2}" d="M1113.03 1162.47l1.97 -24.64c-6.23,9.51 -15.46,16.97 -27.33,22.61l-0.12 19.64c9.68,-2.31 19.89,-9.7 25.48,-17.61z"/>
  
        <path display="${svgConfig.special != 'travicar' && 'none'}" fill="${svgConfig.specialColor}" d="M254.85 715.1l-76.96 0 0 -167.39 -58.67 0 0 -43.4 193.86 0 0 43.4 -58.23 0 0 167.39zm196.79 -210.79c25.21,0 46.16,2.32 62.93,6.94 16.79,4.58 29.31,11.51 37.58,20.67 8.35,9.2 12.45,20.76 12.45,34.68 0,8.17 -1.59,15.75 -4.86,22.64 -3.27,6.94 -8.19,13.23 -14.72,18.89 -6.46,5.71 -14.73,10.73 -24.68,15.16l67.87 91.81 -85.16 0 -50.94 -79.12 -10.02 0 0 79.12 -77.42 0 0 -210.79 86.97 0zm-0.91 41.97l-8.64 0 0 50.78 8.2 0c11.61,0 20.27,-2.31 26.04,-6.94 5.77,-4.62 8.71,-11.56 8.71,-20.91 0,-7.72 -2.88,-13.43 -8.57,-17.27 -5.76,-3.79 -14.27,-5.66 -25.74,-5.66zm330.78 168.82l-12.31 -41.04 -70.21 0 -12.3 41.04 -83.8 0 79.17 -210.79 102.1 0 80.24 210.79 -82.89 0zm-24.3 -84.14l-13.13 -47.18c-1.45,-5.27 -2.81,-10.43 -4.1,-15.45 -1.22,-4.97 -2.35,-9.8 -3.33,-14.32 -0.91,-4.58 -1.83,-8.86 -2.52,-12.79 -0.6,3.73 -1.43,7.87 -2.34,12.39 -0.99,4.53 -2.13,9.25 -3.51,14.22 -1.28,4.97 -2.73,10.09 -4.16,15.36l-13.82 47.77 46.91 0zm379.12 -126.65l-82.89 210.79 -90.09 0 -82.06 -210.79 81.15 0 32.7 103.97c1.53,4.58 3.2,10.28 5.02,16.97 1.82,6.75 3.5,13.44 5.01,20.08 1.44,6.64 2.5,12.05 3.12,16.29 0.91,-4.24 2.11,-9.65 3.64,-16.24 1.59,-6.59 3.25,-13.19 5.15,-19.88 1.84,-6.69 3.5,-12.35 4.94,-16.93l33.17 -104.26 81.14 0zm43.77 210.79l0 -210.79 77.43 0 0 210.79 -77.43 0zm266.92 -170.93c-7.59,0 -14.35,1.52 -20.27,4.57 -5.91,3.05 -10.85,7.43 -14.87,13.19 -4.03,5.71 -7.07,12.69 -9.1,20.91 -2.06,8.22 -3.12,17.47 -3.12,27.8 0,14.32 1.9,26.23 5.69,35.77 3.72,9.6 9.41,16.73 17,21.45 7.52,4.73 17,7.09 28.47,7.09 10.47,0 20.64,-1.28 30.36,-3.89 9.72,-2.6 19.43,-5.7 29.23,-9.35l0 44.98c-10.4,3.93 -21.64,6.84 -33.71,8.75 -12.06,1.92 -24.9,2.91 -38.33,2.91 -25.81,0 -47.6,-4.14 -65.28,-12.45 -17.77,-8.27 -31.13,-20.47 -40.23,-36.56 -9.18,-16.09 -13.75,-35.77 -13.75,-58.99 0,-16.29 2.81,-31.1 8.37,-44.53 5.61,-13.39 13.57,-24.9 24.12,-34.54 10.56,-9.65 23.24,-17.08 38.19,-22.34 14.88,-5.27 31.73,-7.87 50.55,-7.87 14.42,0 28.23,1.18 41.45,3.59 13.29,2.41 26.49,6.15 39.47,11.26l-20.41 41.24c-8.58,-3.94 -17.31,-7.09 -26.05,-9.45 -8.73,-2.36 -17.98,-3.54 -27.78,-3.54zm280.68 170.93l-12.31 -41.04 -70.21 0 -12.29 41.04 -83.8 0 79.17 -210.79 102.1 0 80.23 210.79 -82.89 0zm-24.3 -84.14l-13.12 -47.18c-1.45,-5.27 -2.81,-10.43 -4.11,-15.45 -1.22,-4.97 -2.34,-9.8 -3.33,-14.32 -0.91,-4.58 -1.82,-8.86 -2.51,-12.79 -0.61,3.73 -1.44,7.87 -2.35,12.39 -0.99,4.53 -2.13,9.25 -3.5,14.22 -1.28,4.97 -2.73,10.09 -4.17,15.36l-13.82 47.77 46.91 0zm237.94 -126.65c25.21,0 46.16,2.32 62.93,6.94 16.78,4.58 29.31,11.51 37.58,20.67 8.34,9.2 12.45,20.76 12.45,34.68 0,8.17 -1.59,15.75 -4.86,22.64 -3.27,6.94 -8.2,13.23 -14.72,18.89 -6.47,5.71 -14.73,10.73 -24.69,15.16l67.87 91.81 -85.16 0 -50.94 -79.12 -10.01 0 0 79.12 -77.43 0 0 -210.79 86.98 0zm-0.91 41.97l-8.64 0 0 50.78 8.19 0c11.62,0 20.27,-2.31 26.04,-6.94 5.77,-4.62 8.72,-11.56 8.72,-20.91 0,-7.72 -2.89,-13.43 -8.58,-17.27 -5.75,-3.79 -14.27,-5.66 -25.73,-5.66z"/>
  
      </svg>`
      });

      const marker = L.marker([lat, lng], { icon: svgIcon });
      const speed = parseFloat(position.speed) || 0;
      const velText = `${Units.formatValue(Units.convertSpeed(speed, 'km/h', this.props.res), this.props.res)} ${Units.getSpeedUnit(this.props.res)}`;
      const popupContent = `
        Prefixo: ${airplane.name}<br />
        Data: ${Moment(new Date(position.datetime)).format(this.props.res.FORMAT_DATAHORAMIN)}<br />
        Ângulo: ${angle || 0}<br />
        Velocidade: ${velText}<br />
        Coordenadas:<br />${geo.toDMS(parseFloat(lat), 'latitude', 4)}<br />${geo.toDMS(parseFloat(lng), 'longitude', 4)}
      `;

      marker.bindPopup(popupContent, { closeOnClick: true, autoClose: false });

      this.mapMarkers.push({ marker, index });
      this.featureGroupPoly.addLayer(marker);

      const savedPopupsState = JSON.parse(localStorage.getItem('popupsState')) || [];
      if (savedPopupsState[index]) {
        marker.openPopup();
      }
    }
  };

  clearMapMarkers = () => {
    this.featureGroupPoly.clearLayers();
    this.mapMarkers = [];
  };

  focusOnCurrentAirplane = () => {
    const currentMarker = this.mapMarkers[this.state.currentAirplaneIndex];

    if (currentMarker) {
      const latLng = currentMarker.marker.getLatLng();
      this.map.setView(latLng, 14);

      this.mapMarkers.forEach(({ marker }, idx) => {
        if (marker._icon) {
          marker._icon.style.zIndex = 0;
        }
        if (marker._popup && marker._popup._container) {
          marker._popup._container.style.zIndex = 500;
        }
        marker.openPopup();
      });

      if (currentMarker.marker._icon) {
        currentMarker.marker._icon.style.zIndex = 1000;
      }

      if (currentMarker.marker._popup && currentMarker.marker._popup._container) {
        currentMarker.marker._popup._container.style.zIndex = 1001;
      }

      setTimeout(() => {
        currentMarker.marker.openPopup();
      }, 100);
    }
  };

  handlePreviousAirplane = () => {
    this.setState((prevState) => ({
      currentAirplaneIndex: prevState.currentAirplaneIndex === 0 ? prevState.airplanes.length - 1 : prevState.currentAirplaneIndex - 1,
    }));
  };

  handleNextAirplane = () => {
    this.setState((prevState) => ({
      currentAirplaneIndex: (prevState.currentAirplaneIndex + 1) % prevState.airplanes.length,
    }));
  };

  handleFirstAirplane = () => {
    this.setState({ currentAirplaneIndex: 0 });
  };

  handleLastAirplane = () => {
    this.setState({ currentAirplaneIndex: this.state.airplanes.length - 1 });
  };

  closeAllPopups = () => {
    this.setState({ popupsOpen: false }, () => {
      localStorage.setItem('popupsOpen', JSON.stringify(false));
    });
  };

  togglePopups = () => {
    const currentView = this.map.getCenter();
    const currentZoom = this.map.getZoom();

    if (this.state.popupsOpen) {
      this.mapMarkers.forEach(({ marker }) => {
        marker.closePopup();
      });
    } else {
      this.mapMarkers.forEach(({ marker }) => {
        marker.openPopup();
      });
    }

    this.setState((prevState) => ({ popupsOpen: !prevState.popupsOpen }), () => {
      localStorage.setItem('popupsOpen', JSON.stringify(this.state.popupsOpen));
    });

    this.map.setView(currentView, currentZoom);
  };

  render() {
    const { res } = this.props;

    return (
      <div className='dashboard-card'>
        <div className='divContent'
          style={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem',
            borderRadius: '.8rem',
            backgroundColor: '#343a40',
            overflow: 'hidden',
            height: '100%',
            width: '100%',
            position: 'relative',
          }}
        >
          <div className='cardheader' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <LocationOnIcon style={{ width: '1.7rem', height: '1.7rem', color: '#fa7d34' }} />
              <span className='title' style={{ color: 'white' }}>
                {res.LOCALIZACAO_}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '0 1rem' }}>
              <IconButton onClick={this.handleFirstAirplane} style={{ color: '#fa7d34', padding: '0' }}>
                <FirstPage />
              </IconButton>
              <IconButton onClick={this.handlePreviousAirplane} style={{ color: '#fa7d34', padding: '0' }}>
                <ChevronLeft />
              </IconButton>
              <Typography variant="body1" style={{ color: 'white', padding: '0 1rem' }}>
                {Array.isArray(this.state.airplanes) && this.state.airplanes.length > 0 ? `${this.state.currentAirplaneIndex + 1} / ${this.state.airplanes.length}` : '0 / 0'}
              </Typography>
              <IconButton onClick={this.handleNextAirplane} style={{ color: '#fa7d34', padding: '0' }}>
                <ChevronRight />
              </IconButton>
              <IconButton onClick={this.handleLastAirplane} style={{ color: '#fa7d34', padding: '0' }}>
                <LastPage />
              </IconButton>
              <Tooltip title={this.state.popupsOpen ? "Fechar todos os detalhes das aeronaves" : "Mostrar todos os detalhes das aeronaves"}>
                <IconButton onClick={this.togglePopups} style={{ color: '#fa7d34', padding: '0 0 0 1rem' }}>
                  <Info />
                </IconButton>
              </Tooltip>
            </div>
          </div>
          <div className='cardmiddle' style={{ display: 'block', width: '100%', height: 'calc(100% - 85px)' }}>
            <div ref={this.mapContainerRef} style={{ height: '100%', width: '100%' }}></div>
          </div>
          <div className='cardfooter'>
            <span className='cardfooter-content' style={{ paddingTop: '.7rem' }}>
              <span style={{ display: 'inline-table', paddingLeft: '1rem', width: '50%' }}>
                <span style={{ display: 'table-row', width: '100%', color: '#fa7d34' }}>{res.TOTAL_AERONAVES}</span>
              </span>
              <span style={{ display: 'inline-table', paddingRight: '1rem', width: '50%', fontSize: '1rem', fontWeight: 'bold' }}>
                <span style={{ display: 'table-row', width: '100%' }}>{this.state.airplanes.length}</span>
              </span>
            </span>
          </div>
        </div>
      </div >
    );
  }
}

export default DiaryLocation;